import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { notifApi } from '../../api/notifications.js';

/**
 * Per-event notification preferences. The user can toggle in-app + email
 * channels and pick a digest mode for each event type.
 */

const EVENT_GROUPS = [
  {
    label: 'Issues & projects',
    events: [
      { type: 'issue.assigned_to_me', label: 'Issue assigned to me' },
      { type: 'issue.mentioned',      label: 'I was mentioned in an issue' },
      { type: 'issue.commented',      label: 'New comment on my issue' },
      { type: 'issue.status_changed', label: 'My issue status changed' },
      { type: 'issue.due_soon',       label: 'My issue is due soon' },
    ],
  },
  {
    label: 'Approvals & workflow',
    events: [
      { type: 'approval.action_required', label: 'Approval action required' },
      { type: 'approval.approved',        label: 'My request was approved' },
      { type: 'approval.rejected',        label: 'My request was rejected' },
      { type: 'workflow.assigned',        label: 'Workflow step assigned to me' },
    ],
  },
  {
    label: 'HR',
    events: [
      { type: 'leave.request_submitted',  label: 'Leave request submitted' },
      { type: 'leave.request_approved',   label: 'Leave request approved' },
      { type: 'leave.request_rejected',   label: 'Leave request rejected' },
      { type: 'payroll.run_completed',    label: 'Payroll run completed' },
      { type: 'payroll.payslip_ready',    label: 'Payslip ready' },
    ],
  },
  {
    label: 'Wiki & collaboration',
    events: [
      { type: 'wiki.page_edited', label: 'Page I watch was edited' },
      { type: 'wiki.commented',   label: 'New comment on a wiki page' },
      { type: 'wiki.mentioned',   label: 'Mentioned on a wiki page' },
      { type: 'mention',          label: 'Mentioned anywhere' },
    ],
  },
  {
    label: 'QA & releases',
    events: [
      { type: 'qa.test_failed',      label: 'QA test failed' },
      { type: 'qa.release_approved', label: 'Release approved' },
    ],
  },
  {
    label: 'Procurement',
    events: [
      { type: 'procurement.po_approved',    label: 'PO approved' },
      { type: 'procurement.goods_received', label: 'Goods received' },
    ],
  },
  {
    label: 'System',
    events: [
      { type: 'system.announcement', label: 'System announcement' },
    ],
  },
];

const DIGEST_OPTIONS = [
  { value: 'instant', label: 'Instant' },
  { value: 'hourly',  label: 'Hourly digest' },
  { value: 'daily',   label: 'Daily digest' },
  { value: 'weekly',  label: 'Weekly digest' },
  { value: 'never',   label: 'Never' },
];

export default function NotificationPreferencesPage() {
  const [prefs, setPrefs] = useState(null);
  const [byType, setByType] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set(EVENT_GROUPS.map((g) => g.label)));

  useEffect(() => { reload(); }, []);

  async function reload() {
    const r = await notifApi.preferences();
    const p = r?.preferences;
    setPrefs(p);
    const map = {};
    (p?.events || []).forEach((e) => { map[e.event_type] = e; });
    setByType(map);
    setDirty(false);
  }

  function getRow(type) {
    return byType[type] || {
      event_type: type, in_app_enabled: true, email_enabled: true, push_enabled: false, digest_mode: 'instant',
    };
  }

  function patch(type, field, value) {
    const current = getRow(type);
    setByType((prev) => ({ ...prev, [type]: { ...current, [field]: value } }));
    setDirty(true);
  }

  function toggleGroup(label) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  }

  async function save() {
    setSaving(true);
    try {
      const events = Object.values(byType);
      await notifApi.setEventPreferencesBulk(events);
      reload();
    } finally { setSaving(false); }
  }

  async function resetDefaults() {
    if (!window.confirm('Reset all notification preferences to defaults?')) return;
    const events = EVENT_GROUPS.flatMap((g) => g.events).map((e) => ({
      event_type: e.type, in_app_enabled: true, email_enabled: true, push_enabled: false, digest_mode: 'instant',
    }));
    await notifApi.setEventPreferencesBulk(events);
    reload();
  }

  if (!prefs) return <div className="muted" style={{ padding: 24 }}>Loading…</div>;

  return (
    <section className="page" data-testid="notification-prefs" style={{ padding: '20px 24px', maxWidth: 980, margin: '0 auto' }}>
      <header className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Notification preferences</h1>
        <div style={{ flex: 1 }} />
        <Link to="/profile/notifications" className="btn ghost small">Back to notifications</Link>
      </header>

      {EVENT_GROUPS.map((group) => {
        const isOpen = openGroups.has(group.label);
        return (
          <section key={group.label} className="card" style={{ marginBottom: 12, overflow: 'hidden' }}>
            <header
              onClick={() => toggleGroup(group.label)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', cursor: 'pointer', background: '#f8fafc' }}
              data-testid={`prefs-group-${slug(group.label)}`}
            >
              <span style={{ width: 10 }}>{isOpen ? '▼' : '▶'}</span>
              <strong>{group.label}</strong>
            </header>
            {isOpen && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>
                    <th style={{ padding: '8px 14px', textAlign: 'left' }}>Event</th>
                    <th style={{ padding: '8px 14px', width: 80 }}>In-app</th>
                    <th style={{ padding: '8px 14px', width: 80 }}>Email</th>
                    <th style={{ padding: '8px 14px', width: 160 }}>Digest</th>
                  </tr>
                </thead>
                <tbody>
                  {group.events.map((e) => {
                    const row = getRow(e.type);
                    return (
                      <tr key={e.type} data-testid={`prefs-row-${slug(e.type)}`} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px', fontSize: 13 }}>{e.label}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!!row.in_app_enabled}
                            onChange={(ev) => patch(e.type, 'in_app_enabled', ev.target.checked)}
                            data-testid={`prefs-inapp-${slug(e.type)}`}
                            aria-label={`In-app: ${e.label}`}
                          />
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!!row.email_enabled}
                            onChange={(ev) => patch(e.type, 'email_enabled', ev.target.checked)}
                            data-testid={`prefs-email-${slug(e.type)}`}
                            aria-label={`Email: ${e.label}`}
                          />
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <select
                            value={row.email_enabled ? row.digest_mode : 'instant'}
                            disabled={!row.email_enabled}
                            onChange={(ev) => patch(e.type, 'digest_mode', ev.target.value)}
                            data-testid={`prefs-digest-${slug(e.type)}`}
                            style={{ width: '100%' }}
                          >
                            {DIGEST_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        );
      })}

      <div style={{
        position: 'sticky', bottom: 0, marginTop: 16, padding: '12px 14px',
        display: 'flex', alignItems: 'center', gap: 10, background: '#fff',
        borderTop: '1px solid #e2e8f0',
      }}>
        <button type="button" className="link-btn" onClick={resetDefaults}>Reset to defaults</button>
        <div style={{ flex: 1 }} />
        {dirty && <span className="muted small">Unsaved changes</span>}
        <button className="btn primary" onClick={save} disabled={!dirty || saving} data-testid="prefs-save">
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
      </div>
    </section>
  );
}

function slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
